import {
  TerminalExecuteSsePayloadFromUnknownSchema,
  type TerminalExecutePayload,
} from '@terra/shared-dto-chat-types';
import { z } from 'zod';

const IN_FLIGHT_TERMINAL_EXECUTES_STORAGE_KEY =
  'terra.terminal.in-flight-executes.v1';

const StoredPayloadsSchema = z.array(
  TerminalExecuteSsePayloadFromUnknownSchema,
);

function storage(): Storage | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function writeAll(payloads: TerminalExecutePayload[]): void {
  const store = storage();

  if (!store) {
    return;
  }

  try {
    if (payloads.length === 0) {
      store.removeItem(IN_FLIGHT_TERMINAL_EXECUTES_STORAGE_KEY);

      return;
    }

    store.setItem(
      IN_FLIGHT_TERMINAL_EXECUTES_STORAGE_KEY,
      JSON.stringify(payloads),
    );
  } catch (error: unknown) {
    console.warn(
      '[TerminalExecution] Failed to persist in-flight executes; reload re-binding is unavailable for this turn',
      error,
    );
  }
}

export function readInFlightTerminalExecutes(): TerminalExecutePayload[] {
  const store = storage();

  if (!store) {
    return [];
  }

  const raw = store.getItem(IN_FLIGHT_TERMINAL_EXECUTES_STORAGE_KEY);

  if (!raw) {
    return [];
  }

  try {
    const parsed = StoredPayloadsSchema.safeParse(JSON.parse(raw));

    if (!parsed.success) {
      store.removeItem(IN_FLIGHT_TERMINAL_EXECUTES_STORAGE_KEY);

      return [];
    }

    return parsed.data;
  } catch {
    store.removeItem(IN_FLIGHT_TERMINAL_EXECUTES_STORAGE_KEY);

    return [];
  }
}

function withoutResolvedSecrets(
  payload: TerminalExecutePayload,
): TerminalExecutePayload {
  return {
    ...payload,
    executionCommand: payload.displayCommand,
    command: payload.displayCommand,
  };
}

export function rememberInFlightTerminalExecute(
  payload: TerminalExecutePayload,
): void {
  const others = readInFlightTerminalExecutes().filter(
    (entry) => entry.toolCallId !== payload.toolCallId,
  );

  writeAll([...others, withoutResolvedSecrets(payload)]);
}

export function forgetInFlightTerminalExecute(toolCallId: string): void {
  writeAll(
    readInFlightTerminalExecutes().filter(
      (entry) => entry.toolCallId !== toolCallId,
    ),
  );
}

export function forgetInFlightTerminalExecutesForChat(chatId: string): void {
  writeAll(
    readInFlightTerminalExecutes().filter((entry) => entry.chatId !== chatId),
  );
}
